import {useState, useEffect, memo} from 'react';
import ShootingStar from "@/components/ShootingStar";

const StarrySkyBg = ({showShootingStar, showAurora, showClouds}) => {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);

        const handleChange = (e) => setPrefersReducedMotion(e.matches);
        mediaQuery.addEventListener('change', handleChange);

        const darkModeObserver = new MutationObserver(() => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        });

        darkModeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        setIsDarkMode(document.documentElement.classList.contains('dark'));

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
            darkModeObserver.disconnect();
        };
    }, []);

    const getStarCount = () => {
        const baseCount = isDarkMode ? 150 : 50;
        if (windowWidth < 640) { // Mobile
            return Math.floor(baseCount * 0.4);
        } else if (windowWidth < 1024) { // Tablet
            return Math.floor(baseCount * 0.7);
        }
        return baseCount; // Desktop
    };

    const getCloudCount = () => {
        if (!showClouds) return 0;
        const baseCount = isDarkMode ? 1 : 6;
        if (windowWidth < 640) {
            return Math.max(1, Math.floor(baseCount * 0.3));
        } else if (windowWidth < 1024) {
            return Math.max(1, Math.floor(baseCount * 0.6));
        }
        return baseCount;
    };

    const funCloudShapes = [
        "M30,50 Q40,30 60,40 Q80,20 100,30 Q120,10 140,30 Q160,20 180,40 Q200,30 210,50 Q230,55 215,70 Q220,80 200,75 Q190,90 170,80 Q150,95 130,80 Q110,90 90,75 Q70,85 50,70 Q30,80 25,65 Q10,55 30,50",
        "M40,60 Q60,40 80,50 Q100,30 120,40 Q140,20 160,40 Q180,30 200,50 Q220,40 240,60 Q260,50 270,70 Q280,90 260,85 Q240,95 220,85 Q200,100 180,90 Q160,100 140,85 Q120,95 100,85 Q80,90 60,80 Q40,85 30,75 Q20,65 40,60",
        "M30,50 Q50,30 70,40 Q90,20 110,30 Q130,10 150,30 Q170,20 190,40 Q210,30 230,50 Q250,40 260,60 Q270,80 250,75 Q230,85 210,75 Q190,90 170,80 Q150,95 130,85 Q110,90 90,80 Q70,85 50,75 Q30,80 20,70 Q10,60 30,50"
    ];

    const clouds = Array.from({ length: getCloudCount() }, (_, i) => ({
        id: i,
        top: `${Math.random() * 90}%`,
        scale: 0.6 + Math.random() * 1.2,
        speed: 80 + Math.random() * 35,
        delay: Math.random() * -400,
        shape: funCloudShapes[Math.floor(Math.random() * funCloudShapes.length)]
    }));

    const stars = Array.from({ length: getStarCount() }, (_, i) => ({
        id: i,
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        size: Math.random() * 8 + 1,
        animationDelay: `${Math.random() * 3}s`
    }));

    // Rest of the component remains the same
    return (
        <>
            {isDarkMode && showShootingStar ? (<ShootingStar />) : (<></>)}
            <style>{`
                @media (prefers-reduced-motion: no-preference) {
                    @keyframes twinkle {
                        0%, 100% { opacity: 0.3; transform: scale(1); }
                        50% { opacity: 1; transform: scale(1.3); }
                    }
                    @keyframes float-clouds {
                        from { transform: translateX(-100%); }
                        to { transform: translateX(100vw); }
                    }
                    @keyframes aurora {
                        0% { transform: translate(-25%, 0) skewX(5deg); opacity: 0.7; }
                        25% { transform: translate(0%, -30px) skewX(-5deg); opacity: 0.8; }
                        50% { transform: translate(25%, 0) skewX(5deg); opacity: 0.9; }
                        75% { transform: translate(0%, -30px) skewX(-5deg); opacity: 0.8; }
                        100% { transform: translate(-25%, 0) skewX(5deg); opacity: 0.7; }
                    }
                    .aurora { animation: aurora 30s ease-in-out infinite; }
                    .star { animation: twinkle 3s ease-in-out infinite; }
                }
            `}</style>

            <div className="fixed inset-0 overflow-hidden -z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-200 via-blue-400 to-blue-600 dark:from-[rgba(2,0,36,0.7)] dark:via-[rgba(45,14,87,0.7)] dark:to-[rgba(9,9,121,0.7)]" />

                {!prefersReducedMotion && clouds.map((cloud) => (
                    <div
                        key={cloud.id}
                        className="absolute"
                        style={{
                            top: cloud.top,
                            animation: `float-clouds ${cloud.speed}s linear infinite`,
                            animationDelay: `${cloud.delay}s`,
                        }}
                    >
                        <svg
                            width="300"
                            height="150"
                            viewBox="0 0 300 150"
                            style={{
                                transform: `scale(${cloud.scale})`,
                            }}
                            >
                            <defs>
                                <filter id="cloud-blur">
                                <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
                                <feComponentTransfer>
                                    <feFuncA type="linear" slope="0.7" />
                                </feComponentTransfer>
                                </filter>

                                <radialGradient id="cloud-gradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                                <stop offset="0%" style={{ stopColor: isDarkMode ? '#cfcced' : 'white', stopOpacity: 0.9 }} />
                                <stop offset="80%" style={{ stopColor: isDarkMode ? '#cfcced' : 'white', stopOpacity: 0.7 }} />
                                <stop offset="100%" style={{ stopColor: isDarkMode ? '#cfcced' : 'white', stopOpacity: 0.5 }} />
                                </radialGradient>
                            </defs>

                            <g>
                                <path
                                d={cloud.shape}
                                fill="url(#cloud-gradient)"
                                filter="url(#cloud-blur)"
                                />
                            </g>
                        </svg>
                    </div>
                ))}

                {[...Array(isDarkMode && showAurora ? 2 : 0)].map((_, i) => (
                    <div
                        key={`aurora-${i}`}
                        className="absolute w-full aurora h-96 opacity-70"
                        style={{
                            background: `radial-gradient(ellipse at center, rgba(${40 + i * 10},${180 + i * 20},${100 + i * 30},0.8) 0%, transparent 70%)`,
                            top: `${20 * i}%`,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            animationDelay: `${i * 6}s`,
                            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                            filter: 'blur(30px)'
                        }}
                    />
                ))}

                {stars.map((star) => (
                    <div
                        key={star.id}
                        className="absolute star bg-slate-50 dark:bg-white"
                        style={{
                            top: star.top,
                            left: star.left,
                            width: `${star.size}px`,
                            height: `${star.size}px`,
                            clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                            animationDelay: star.animationDelay,
                            opacity: 0.7
                        }}
                    />
                ))}
            </div>
        </>
    );
};

interface StarrySkyBackgroundProps {
    showShootingStar: boolean;
    showAurora: boolean;
    showClouds: boolean;
}

const StarrySkyBackground = memo(({showShootingStar, showAurora, showClouds}: StarrySkyBackgroundProps) => (
    <StarrySkyBg showShootingStar={showShootingStar} showAurora={showAurora} showClouds={showClouds}/>
));

export default StarrySkyBackground;